import { ImageWeb } from '@nucleus/types/media/image';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { formatSrcSet } from '../lib/media';
import { DurationBadge } from './DurationBadge';
import { Thumbnail } from './Thumbnail';

declare module '@nucleus/sermon-theme-elements' {
  interface SermonThemeElementsTheme {
    MediaListItem?: {
      color?: string;
      background?: string;
      hover?: {
        background?: string;
        shadow?: string;
      };
    };
  }
}

interface Props {
  actions?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  duration?: React.ReactNode;
  image?: ImageWeb;
  isImageVisible?: boolean;
  style?: React.CSSProperties;
  to: string;
}

export const MediaListItem = (props: Props): JSX.Element => {
  const navigate = useNavigate();
  const isImageVisible = props.isImageVisible !== false;

  return (
    <Card
      className={props.className}
      style={props.style}
      onClick={(event) => !event.defaultPrevented && navigate(props.to)}
    >
      {isImageVisible && (
        <Media>
          <Link to={props.to}>
            <ThumbnailContainer>
              <Thumbnail
                src={props.image?.src}
                srcSet={formatSrcSet(props.image?.srcSet)}
                blurHash={props.image?.blurHash}
                focalPoint={props.image?.focalPoint}
              />
              <Button />
              {props.duration && <DurationBadge>{props.duration}</DurationBadge>}
            </ThumbnailContainer>
          </Link>
        </Media>
      )}
      <Meta>
        {props.children}
        <Actions onClick={(event) => event.stopPropagation()}>{props.actions}</Actions>
      </Meta>
    </Card>
  );
};

const Media = styled.div`
  flex: 0 0 320px;
  width: 320px;
  cursor: default;
`;

const Meta = styled.div`
  flex: 1 1 200px;
  padding-right: 50px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme._sermonThemeElements.MediaListItem?.background};
  color: ${({ theme }) => theme._sermonThemeElements.MediaListItem?.color};
  border-radius: 10px;
  min-height: 210px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  padding: 15px;
  gap: 30px;
  position: relative;
  cursor: pointer;

  transition:
    background 250ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 250ms cubic-bezier(0.23, 1, 0.32, 1);

  :hover {
    background: ${({ theme }) => theme._sermonThemeElements.MediaListItem?.hover?.background};
    box-shadow: 0px 12px 36px ${({ theme }) => theme._sermonThemeElements.MediaListItem?.hover?.shadow};
  }

  ${Meta}:first-child {
    padding-top: 10px;
    padding-left: 30px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
    gap: 20px;

    & > div:first-child {
      flex: 0 0 100%;
      width: 100%;
    }
    & > div:last-child {
      flex: 1 1 100%;
      max-width: 100%;
    }
  }
`;

const Button = styled.button`
  display: none;
`;

const ThumbnailContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  ${Button} {
    position: absolute;
  }

  ${DurationBadge} {
    position: absolute;
    right: 0;
    bottom: 0;
    margin: 10px;
  }
`;

const Actions = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
`;
